import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit, inject, ViewEncapsulation } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { NgSelectModule } from '@ng-select/ng-select'
import { Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { BehaviorSubject, Observable, Subject, combineLatest, interval } from 'rxjs'
import { map, switchMap, takeUntil } from 'rxjs/operators'

import { selectAllUsers } from '../../../store/user/user.selectors'
import { loadUsers } from '../../../store/user/user.actions'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { PaginationComponent } from '../template/pagination/pagination.component'

import {
  CreateCampaignPayload,
  NotificationCampaign,
  NotificationCampaignService,
  NotificationChannel,
  OccasionTemplate
} from '../../../services/notification-campaign.service'

import { selectAllBusinesses } from '../../../store/business/business.selectors'
import { Business } from '../../../store/business/business.state'
import { loadBusinesses } from '../../../store/business/business.actions'

import { CKEditorModule } from '@ckeditor/ckeditor5-angular'
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading,
  Font,
  Link,
  List,
  Alignment,
  BlockQuote,
  Indent,
  RemoveFormat
} from 'ckeditor5'

import type { EditorConfig } from 'ckeditor5'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { eps } from '../../../services/url.service'

@Component({
  selector: 'app-notification-campaign',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    CapitalizePipe,
    PaginationComponent,
    CKEditorModule,
    RouterModule
  ],
  templateUrl: './notification-campaign.component.html',
  styleUrls: ['./notification-campaign.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NotificationCampaignComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private router = inject(Router)
  private destroy$ = new Subject<void>()

  users$: Observable<any[]> = this.store.select(selectAllUsers)
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)

  campaigns: NotificationCampaign[] = []

  totalCount = 0
  currentPage = 1
  pageLimit = 10
  pageLastRecord = this.pageLimit
  loading = false

  filterStatus = ''

  readonly statusOptions = [
    'scheduled',
    'queued',
    'running',
    'success',
    'partial_failed',
    'failed',
    'stopped',
    'cancelled'
  ]

  // =========================
  // CREATE MODAL
  // =========================

  showCreateModal = false
  submitting = false
  createError = ''

  form = this.emptyForm()

  readonly timezones = [
    'UTC',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Singapore',
    'Europe/London',
    'America/New_York',
    'America/Los_Angeles'
  ]

  readonly PUSH_TITLE_MAX_LENGTH = 65
  readonly PUSH_MESSAGE_MAX_LENGTH = 240

  // =========================
  // CKEDITOR
  // =========================

  public Editor = ClassicEditor

  public editorConfig: EditorConfig = {
    licenseKey: 'GPL',

    plugins: [
      Essentials,
      Paragraph,
      Heading,
      Bold,
      Italic,
      Underline,
      Strikethrough,
      Font,
      Link,
      List,
      Alignment,
      BlockQuote,
      Indent,
      RemoveFormat
    ],

    toolbar: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'alignment',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'link',
      'blockQuote',
      '|',
      'removeFormat'
    ],

    alignment: {
      options: ['left', 'center', 'right', 'justify'] as any
    },

    root: {
      placeholder: 'Write your message...'
    }
  }

  private editorInstance: ClassicEditor | null = null

  // =========================
  // TEMPLATES
  // =========================

  templates: OccasionTemplate[] = []

  readonly availablePlaceholders = [
    { key: '{firstName}', label: 'First Name' },
    { key: '{lastName}', label: 'Last Name' },
    { key: '{years}', label: 'Years of Service' },
    { key: '{businessName}', label: 'Business Name' }
  ]
  private templateBusinessIdSubject = new BehaviorSubject<string | null>(null)
  templateBusinessId$ = this.templateBusinessIdSubject.asObservable()
  selectedTemplateBusinessId: string | null = null

  // ✅ ADD — business-restricted users list (template ke business ke hisaab se filtered)
  filteredUsers$: Observable<any[]> = combineLatest([this.users$, this.templateBusinessId$]).pipe(
    map(([users, businessId]) =>
      businessId ? users.filter(u => u.businessId === businessId) : users
    )
  )

  constructor(
    private campaignService: NotificationCampaignService,
    private sanitizer: DomSanitizer
  ) {}

  // =========================
  // LIFECYCLE
  // =========================

  ngOnInit(): void {
    this.loadCampaigns()
    this.loadAllBusinesses()
    this.loadAllUsers()
    this.loadTemplates()

    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const hasActiveCampaign = this.campaigns.some(c =>
            ['running', 'queued', 'scheduled'].includes(c.status)
          )

          return hasActiveCampaign ? this.campaignService.list(this.buildQuery()) : []
        })
      )
      .subscribe((res: any) => {
        if (res?.data) {
          this.campaigns = res.data
          this.totalCount = res.total
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  // =========================
  // CAMPAIGN LIST
  // =========================

  private buildQuery(): any {
    const query: any = {
      page: this.currentPage,
      limit: this.pageLimit
    }

    if (this.filterStatus) {
      query.status = this.filterStatus
    }

    return query
  }

  loadCampaigns(): void {
    this.loading = true

    this.campaignService.list(this.buildQuery()).subscribe({
      next: res => {
        this.campaigns = res.data
        this.totalCount = res.total
        this.loading = false
        this.updatePageLastRecord()
      },
      error: () => {
        this.loading = false
      }
    })
  }

  onFilterChange(): void {
    this.currentPage = 1
    this.loadCampaigns()
  }

  onPageChange(newPage: number): void {
    if (newPage <= 0) {
      return
    }

    this.currentPage = newPage
    this.updatePageLastRecord()
    this.loadCampaigns()
  }

  onPageLimitChange(): void {
    this.currentPage = 1
    this.updatePageLastRecord()
    this.loadCampaigns()
  }

  private updatePageLastRecord(): void {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  // =========================
  // LOAD DATA
  // =========================

  loadAllBusinesses(): void {
    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams({
          disabled: 'false'
        }).toString()
      })
    )
  }

  loadAllUsers(): void {
    this.store.dispatch(loadUsers())
  }

  loadTemplates(): void {
    this.campaignService.listTemplates().subscribe({
      next: res => {
        this.templates = res
      },
      error: () => {
        this.templates = []
      }
    })
  }

  // =========================
  // SEARCH
  // =========================

  customBusinessSearchFn = (term: string, item: any): boolean => {
    return `${item.businessName}`.toLowerCase().includes(term.toLowerCase())
  }

  customUserSearchFn = (term: string, item: any): boolean => {
    return `${item.firstName} ${item.lastName} ${item.email}`
      .toLowerCase()
      .includes(term.toLowerCase())
  }

  // =========================
  // FORM
  // =========================

  private emptyForm() {
    return {
      title: '',
      message: '',

      // Only one can be selected
      channel: 'push' as NotificationChannel | null,

      businessIds: [] as string[],
      individualUserIds: [] as string[],

      sendMode: 'now' as 'now' | 'later' | 'recurring',

      scheduledDate: '',
      scheduledTime: '',

      timezone: 'UTC',

      scheduleType: 'daily' as 'daily' | 'birthday' | 'work_anniversary',

      contentMode: 'custom' as 'template' | 'custom',

      selectedTemplateId: ''
    }
  }

  openCreateModal(): void {
    this.form = this.emptyForm()
    this.createError = ''
    this.submitting = false
    this.editorInstance = null
    this.showCreateModal = true
  }

  closeCreateModal(): void {
    if (this.submitting) {
      return
    }

    this.showCreateModal = false
    this.createError = ''
    this.editorInstance = null
  }

  // =========================
  // CHANNEL
  // =========================

  onChannelChange(): void {
    this.createError = ''
  }

  get isPushSelected(): boolean {
    return this.form.channel === 'push'
  }

  get isEmailSelected(): boolean {
    return this.form.channel === 'email'
  }

  // =========================
  // CONTENT MODE
  // =========================

  onContentModeChange(): void {
    if (this.form.contentMode === 'custom') {
      this.form.selectedTemplateId = ''
      this.form.title = ''
      this.form.message = ''
      this.form.channel = null
      this.selectedTemplateBusinessId = null
      this.templateBusinessIdSubject.next(null)
    } else {
      this.form.selectedTemplateId = ''
      this.form.title = ''
      this.form.message = ''
      this.form.channel = null
    }

    this.createError = ''
  }

  // ✅ ADD — helper for template display in dropdown/UI
  get isBusinessLocked(): boolean {
    return !!this.selectedTemplateBusinessId
  }

  // onTemplateSelect(): void {
  //   const template = this.templates.find(t => t.id === this.form.selectedTemplateId)

  //   if (!template) {
  //     return
  //   }

  //   this.form.title = template.title
  //   this.form.message = template.message

  //   // Template channel ko force nahi kar rahe.
  //   // User manually Push OR Email select karega.
  //   this.createError = ''
  // }
  onTemplateSelect(): void {
    const template = this.templates.find(t => t.id === this.form.selectedTemplateId)
    if (!template) return

    this.form.title = template.title
    this.form.message = template.message
    this.form.channel = template.channel
    this.form.selectedTemplateId = template.id
    if (template.businessId) {
      // ✅ Business-specific template — us business tak campaign restrict karo
      this.selectedTemplateBusinessId = template.businessId
      this.form.businessIds = [template.businessId]
      this.form.individualUserIds = [] // reset — purani selection us business ki nahi ho sakti
      this.templateBusinessIdSubject.next(template.businessId)
    } else {
      // ✅ Default/global template — koi restriction nahi
      this.selectedTemplateBusinessId = null
      this.templateBusinessIdSubject.next(null)
    }
    this.createError = ''
  }

  // =========================
  // PLACEHOLDER
  // =========================

  insertPlaceholder(placeholder: string): void {
    if (!this.editorInstance) {
      return
    }

    const editor = this.editorInstance

    editor.model.change((writer: any) => {
      const insertPosition = editor.model.document.selection.getFirstPosition()

      writer.insertText(placeholder, insertPosition)
    })

    editor.editing.view.focus()
  }

  onEditorReady(editor: any): void {
    this.editorInstance = editor
  }

  // =========================
  // PREVIEW
  // =========================

  get messagePreview(): SafeHtml {
    const resolved = this.replacePlaceholders(this.form.message)

    return this.sanitizer.bypassSecurityTrustHtml(resolved)
  }

  get titlePreview(): string {
    return this.replacePlaceholders(this.form.title)
  }

  private replacePlaceholders(value: string): string {
    return (value || '')
      .replace(/{firstName}/g, 'John')
      .replace(/{lastName}/g, 'Doe')
      .replace(/{years}/g, '3')
      .replace(/{businessName}/g, 'Acme Corp')
  }

  // =========================
  // CHARACTER COUNT
  // =========================

  getPlainTextLength(html: string): number {
    const div = document.createElement('div')

    div.innerHTML = html || ''

    return (div.textContent || div.innerText || '').trim().length
  }

  get titleCharCount(): number {
    return (this.form.title || '').length
  }

  get messageCharCount(): number {
    return this.getPlainTextLength(this.form.message)
  }

  get isTitleOverLimit(): boolean {
    return this.isPushSelected && this.titleCharCount > this.PUSH_TITLE_MAX_LENGTH
  }

  get isMessageOverLimit(): boolean {
    return this.isPushSelected && this.messageCharCount > this.PUSH_MESSAGE_MAX_LENGTH
  }

  // =========================
  // SEND MODE
  // =========================

  onSendModeChange(mode: 'now' | 'later' | 'recurring'): void {
    this.form.sendMode = mode
    this.createError = ''

    if (mode === 'now') {
      this.form.scheduledDate = ''
      this.form.scheduledTime = ''
      this.form.scheduleType = 'daily'
    }

    if (mode === 'later') {
      this.form.scheduleType = 'daily'
    }

    if (mode === 'recurring') {
      this.form.scheduledDate = ''

      if (!this.form.scheduledTime) {
        this.form.scheduledTime = '09:00'
      }
    }
  }

  // =========================
  // SCHEDULE
  // =========================

  get minScheduleDate(): string {
    return this.formatDateForInput(new Date())
  }

  get minScheduleTime(): string {
    return this.formatTimeForInput(new Date())
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  private formatTimeForInput(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${hours}:${minutes}`
  }

  onScheduleDateChange(): void {
    if (this.form.scheduledDate < this.minScheduleDate) {
      this.form.scheduledDate = this.minScheduleDate
    }

    if (this.form.scheduledDate > this.minScheduleDate) {
      return
    }

    if (this.form.scheduledTime && this.form.scheduledTime < this.minScheduleTime) {
      this.form.scheduledTime = this.minScheduleTime
    }
  }

  onScheduleTypeChange(): void {
    if (this.form.scheduleType === 'birthday' || this.form.scheduleType === 'work_anniversary') {
      this.form.scheduledDate = ''
    }
  }

  // =========================
  // SUBMIT CAMPAIGN
  // =========================

  submitCampaign(): void {
    this.createError = ''

    // -------------------------
    // Channel
    // -------------------------

    if (!this.form.channel) {
      this.createError = 'Please select a channel.'
      return
    }

    // -------------------------
    // Title
    // -------------------------

    const title = (this.form.title || '').trim()

    if (!title) {
      this.createError = 'Please enter a title.'
      return
    }

    // -------------------------
    // Message
    // -------------------------

    // const message = (this.form.message || '').trim()
    const message =
      this.form.channel === 'push' ? this.getPlainText(this.form.message) : this.form.message.trim()

    const messageText = this.getPlainTextLength(message)

    if (!messageText) {
      this.createError = 'Please enter a message.'
      return
    }

    // -------------------------
    // Push validation
    // -------------------------

    if (this.form.channel === 'push') {
      if (title.length > this.PUSH_TITLE_MAX_LENGTH) {
        this.createError =
          `Title exceeds ${this.PUSH_TITLE_MAX_LENGTH} characters. ` + `Current: ${title.length}`

        return
      }

      if (messageText > this.PUSH_MESSAGE_MAX_LENGTH) {
        this.createError =
          `Message exceeds ${this.PUSH_MESSAGE_MAX_LENGTH} characters. ` + `Current: ${messageText}`

        return
      }
    }

    // -------------------------
    // Recipients
    // -------------------------

    // if (!this.form.businessIds.length && !this.form.individualUserIds.length) {
    //   this.createError = 'Please select at least one business or individual user.'

    //   return
    // }

    // -------------------------
    // Scheduled At
    // -------------------------

    let scheduledAt: string | undefined

    if (this.form.sendMode === 'later') {
      if (!this.form.scheduledDate || !this.form.scheduledTime) {
        this.createError = 'Please select a schedule date and time.'

        return
      }

      const combined = new Date(`${this.form.scheduledDate}T${this.form.scheduledTime}:00`)

      if (isNaN(combined.getTime()) || combined <= new Date()) {
        this.createError = 'Scheduled time must be in the future.'

        return
      }

      scheduledAt = combined.toISOString()
    }

    // -------------------------
    // Recurring validation
    // -------------------------

    if (this.form.sendMode === 'recurring') {
      if (!this.form.scheduledTime) {
        this.createError = 'Please select a recurring time.'

        return
      }

      if (!this.form.scheduleType) {
        this.createError = 'Please select a repeat type.'

        return
      }
    }

    // -------------------------
    // Payload
    // -------------------------

    const payload: CreateCampaignPayload = {
      title,
      message,
      channel: this.form.channel,

      businessIds: this.form.businessIds,
      individualUserIds: this.form.individualUserIds,

      scheduledAt,

      timezone: this.form.timezone,

      scheduleType: this.form.sendMode === 'recurring' ? this.form.scheduleType : 'once',

      scheduleTime: this.form.sendMode === 'recurring' ? this.form.scheduledTime : null,

      isRecurring: this.form.sendMode === 'recurring'
    }

    // -------------------------
    // API
    // -------------------------

    this.submitting = true

    this.campaignService.create(payload).subscribe({
      next: () => {
        this.submitting = false
        this.showCreateModal = false
        this.form = this.emptyForm()
        this.loadCampaigns()
      },

      error: (err: any) => {
        this.submitting = false

        this.createError = err?.error?.message || err?.message || 'Failed to create campaign.'
      }
    })
  }

  // =========================
  // ROW ACTIONS
  // =========================

  canStop(status: string): boolean {
    return status === 'running'
  }

  canCancel(status: string): boolean {
    return status === 'scheduled'
  }

  canRetry(status: string): boolean {
    return ['failed', 'partial_failed', 'stopped'].includes(status)
  }

  stop(c: NotificationCampaign): void {
    this.campaignService.stop(c.id).subscribe(() => this.loadCampaigns())
  }

  cancel(c: NotificationCampaign): void {
    this.campaignService.cancel(c.id).subscribe(() => this.loadCampaigns())
  }

  retry(c: NotificationCampaign): void {
    this.campaignService.retry(c.id).subscribe(() => this.loadCampaigns())
  }

  viewHistory(c: NotificationCampaign): void {
    this.router.navigate([eps.notificationCampaign.base, c.id])
  }

  statusClass(status: string): string {
    return `status-badge status-${status}`
  }

  channelLabel(channel: NotificationChannel): string {
    return {
      push: 'Push',
      email: 'Email',
      both: 'Push + Email'
    }[channel]
  }

  progressPercent(c: NotificationCampaign): number {
    if (!c.totalRecipients) {
      return 0
    }

    return Math.round((c.processedRecipients / c.totalRecipients) * 100)
  }

  sourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'work_anniversary_cron':
        return 'Work Anniversary'

      case 'birthday_cron':
        return 'Birthday'

      case 'super_admin':
        return 'Super Admin'

      case 'business':
        return 'Business'

      default:
        return 'Manual'
    }
  }

  getPlainText(html: string): string {
    if (!html) return ''

    const div = document.createElement('div')
    div.innerHTML = html

    return (div.textContent || div.innerText || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  goToTemplates(): void {
    this.router.navigate([eps.notificationCampaign.templatesPath])
  }
}
